import React from "react";
import { withNamespaces } from "react-i18next";
import { Text, View, Link } from "@react-pdf/renderer";
import styles from "../../Styles";
import HandIcon from "../../components/HandIcon";


const DownloadDetails = ({
  t,
  supplierData,
  detailsQuery,
  user,
  dateUtils,
  INTERNATIONAL_DATE_FORMAT,
  nowText,
}) => {
  const data = detailsQuery?.data ?? detailsQuery ?? {};
  const company = data?.company ?? {};
  const financial_summary = data?.financial_summary ?? null;
  const management_team = data?.management_team ?? [];
  const company_shareholders = data?.company_shareholders ?? [];

  // --- Important notes 
  const notes = [
    "Illustration Only: The specific datasets shown in this report (e.g., company data, employee numbers, financial figures) are fictitious or selected for demonstration purposes only. They are intended solely to give you an impression of how the information will be presented in the final report.",
    "No Guarantee: The actual reports generated for you will contain the relevant datasets depending on the current availability of the data from the respective content partners. There is no guarantee that every dataset shown here will be available in the same form for every target company.",
    "Data Accuracy: The accuracy, completeness, and timeliness of the actual datasets are subject to the respective content partners.",
    "Confidentiality and Non-Transferability: This example report is confidential and intended solely for your internal evaluation.",
    "The onward transmission or duplication of this report to third parties (regardless of whether they are competitors or other external parties) is not permitted.",
  ];

  // --- Downloaded By ---
  const currentUser = user?.user ?? user;
  const downloadedBy =
    currentUser && (currentUser?.first_name || currentUser?.last_name)
      ? `${currentUser?.first_name ?? ""} ${currentUser?.last_name ?? ""}`.trim()
      : "-";

  // --- Download On  ---
  const downloadOn = nowText ?? "-";

  // --- Report Status Date ---
  // Prefer organizationData.attributes.lastUpdate.date, else supplier creationDate.
  const org = (() => {
    try {
      return company?.organizationData ? JSON.parse(company.organizationData) : null;
    } catch {
      return null;
    }
  })();

  const lastUpdateDate = org?.data?.attributes?.lastUpdate?.date; 
  const reportStatusDate =
    lastUpdateDate && dateUtils
      ? dateUtils.convertDateToDate(lastUpdateDate, INTERNATIONAL_DATE_FORMAT)
      : supplierData?.creationDate && dateUtils
      ? dateUtils.convertDateToDate(supplierData.creationDate, INTERNATIONAL_DATE_FORMAT)
      : "-";


  const datasets = [];

  if (company?.organizationData) datasets.push({ label: "Companyinfo_registry_details" });
  if (company?.ultimateShareholderData) datasets.push({ label: "Transparency_register" });
  if (company?.shareholderGraphData) datasets.push({ label: "Trade_list_register" });
  if (company?.officialDocumentData) datasets.push({ label: "Legal_documents" });

  if (financial_summary) datasets.push({ label: "Financial_summary" });
  if (Array.isArray(management_team) && management_team.length)
    datasets.push({ label: "Management_team" });
  if (Array.isArray(company_shareholders) && company_shareholders.length)
    datasets.push({ label: "Company_shareholders" });


  const rows = [
    {
      title: "Company Info",
      downloadedBy,
      downloadOn,
      reportStatusDate,
      datasets,
    },
  ];

  const renderDatasetDetails = (list) => {
    const safeList = Array.isArray(list) ? list : [];
    if (safeList.length === 0) return <Text style={styles.tableCell}>-</Text>;


    return (
      <Text style={styles.tableCell}>
        {safeList.map((ds, idx) => {
          const label = ds?.label ?? "-";
          const url = ds?.url;

          return url ? (
            <Link key={idx} src={url}>
              {t(label)}
            </Link>
          ) : (
            <Text key={idx}>
              {t(label)} <br/>
            </Text>
          );
        })}
      </Text>
    );
  };

  return (
    <View style={styles.section}>
      {/* Important notes */}
      <View style={styles.note}>
        <HandIcon />
        <Text style={styles.ml1}>{t("Important Notes:")}</Text>
      </View>

      {notes.map((line, idx) => (
        <View key={idx} style={styles.note}>
          <Text style={styles.ml1}>{t(line)}</Text>
        </View>
      ))}

      {/* DOWNLOAD DETAILS title bar */}
      <Text style={styles.sectionSeparator}>{t("DOWNLOAD DETAILS")}</Text>

      {/* table */}
      <View style={styles.table}>
        {/* header */}
        <View style={styles.tableRow}>
          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Title")}
            </Text>
          </View>

          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Downloaded By")}
            </Text>
          </View>

          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Download On (dd/mm/yyyy; hh:mm)")}
            </Text>
          </View>

          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Report Status Date")}
            </Text>
          </View>

          <View style={{ ...styles.tableColHeader, ...styles.borderRightNone }}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Dataset Details")}
            </Text>
          </View>
        </View>

        {/* rows */}
        {rows.map((r, i) => {
          const rowStyle = {
            ...styles.tableRow,
            ...(i === rows.length - 1 ? styles.tableLastRow : {}),
          };

          return (
            <View style={rowStyle} key={i}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{t(r.title ?? "-")}</Text>
              </View>

              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{r.downloadedBy ?? "-"}</Text>
              </View>

              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{r.downloadOn ?? "-"}</Text>
              </View>

              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{r.reportStatusDate ?? "-"}</Text>
              </View>

              <View style={{ ...styles.tableCol, ...styles.borderRightNone }}>
                {renderDatasetDetails(r.datasets)}
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

export default withNamespaces()(DownloadDetails);
